/* @flow */
import React, { Component } from 'react';
import { css, names } from 'linaria';

import Colors from '../../constants/Colors';
import Link from './Link';
import MediaQueries from '../../constants/MediaQueries';
import Icon from './Icon';
import type { IconName } from './Icon';

export type ButtonTheme = $Keys<typeof ThemeColors>;

class Button extends Component<{
  as?: string,
  icon?: IconName,
  className?: string,
  style?: {},
  theme: ButtonTheme,
  disabledTheme?: ButtonTheme,
  disabled?: boolean,
  bordered?: boolean,
  children?: any,
  to?: string,
  onClick?: Function,
}> {
  static defaultProps = {
    as: 'button',
    theme: 'indigoLight',
    bordered: true,
    disabled: false,
  };

  onClick = e => {
    let { disabled, onClick } = this.props;
    if (!disabled && onClick) {
      e.preventDefault();
      onClick(e);
    }
  };

  getThemeClassName(theme: ButtonTheme, bordered: ?boolean, disabled: ?boolean) {
    return ThemeStyles[bordered ? 'bordered' : 'regular'][disabled ? 'disabled' : 'enabled'][theme];
  }

  render() {
    let {
      as,
      children,
      icon,
      disabled,
      theme,
      disabledTheme,
      bordered,
      to,
      className,
      ...rest
    } = this.props;

    const Comp = to ? Link : as;
    return (
      <Comp
        className={names(
          Styles.Button,
          this.getThemeClassName(disabled ? disabledTheme || 'gray' : theme, bordered, disabled),
          className
        )}
        to={to}
        disabled={disabled}
        onClick={this.onClick}
        {...rest}>
        {children}
        {icon && (
          <div className={Styles.Icon}>
            <Icon name={icon} />
          </div>
        )}
      </Comp>
    );
  }
}

const ThemeColors = {
  yellow: {
    button: Colors.Yellow,
    buttonHover: Colors.Yellow,
    buttonHoverShadow: Colors.opacity(Colors.Yellow, 0.45),
    buttonActive: Colors.darken(Colors.Yellow, 0.2),
    text: Colors.IndigoDarkest,
    borderedText: Colors.Yellow,
  },
  indigo: {
    button: Colors.IndigoDark,
    buttonHover: Colors.darken(Colors.IndigoDark, 0.1),
    buttonHoverShadow: Colors.opacity(Colors.IndigoDark, 0.35),
    buttonActive: Colors.darken(Colors.IndigoDark, 0.1),
    text: Colors.White,
    borderedText: Colors.IndigoDark,
  },
  indigoLight: {
    button: Colors.opacity(Colors.IndigoDark, 0.2),
    buttonHover: Colors.darken(Colors.IndigoDark, 0.1),
    buttonHoverShadow: Colors.opacity(Colors.IndigoDark, 0.35),
    buttonActive: Colors.darken(Colors.IndigoDark, 0.1),
    text: Colors.Indigo,
    borderedText: Colors.Black,
  },
  white: {
    button: Colors.opacity(Colors.White, 0.4),
    buttonHover: Colors.Violet,
    buttonHoverShadow: Colors.opacity(Colors.Violet, 0.35),
    buttonActive: Colors.darken(Colors.Violet, 0.1),
    text: Colors.White,
    borderedText: Colors.White,
  },
  violet: {
    button: Colors.Violet,
    buttonHover: Colors.Violet,
    buttonHoverShadow: Colors.opacity(Colors.Violet, 0.35),
    buttonActive: Colors.darken(Colors.Violet, 0.1),
    text: Colors.White,
    borderedText: Colors.Violet,
  },
  gray: {
    button: Colors.GrayLight,
    buttonHover: Colors.GrayLight,
    buttonHoverShadow: 'transparent',
    buttonActive: Colors.GrayLight,
    text: Colors.Black,
    borderedText: Colors.GrayLight,
  },
  red: {
    button: Colors.RedError,
    buttonHover: Colors.RedError,
    buttonHoverShadow: 'transparent',
    buttonActive: Colors.RedError,
    text: Colors.White,
    borderedText: Colors.RedError,
  },
  publicStorage: {
    button: Colors.PublicStorage,
    buttonHover: Colors.PublicStorage,
    buttonHoverShadow: 'transparent',
    buttonActive: Colors.PublicStorage,
    text: Colors.White,
    borderedText: Colors.Black,
  },
  extraSpaceStorage: {
    button: Colors.ExtraSpaceStorage,
    buttonHover: Colors.ExtraSpaceStorage,
    buttonHoverShadow: 'transparent',
    buttonActive: Colors.ExtraSpaceStorage,
    text: Colors.White,
    borderedText: Colors.Black,
  },
  a1Storage: {
    button: Colors.A1Storage,
    buttonHover: Colors.A1Storage,
    buttonHoverShadow: 'transparent',
    buttonActive: Colors.A1Storage,
    text: Colors.White,
    borderedText: Colors.Black,
  },
};

const Styles = {
  Button: css`
    border-radius: 5px;
    color: ${Colors.Indigo};
    display: inline-block;
    font-size: 18px;
    font-weight: 700;
    padding: 10px 24px 10px 24px;
    text-align: center;
    -webkit-tap-highlight-color: transparent;
    ${MediaQueries.Medium} {
      padding: 13px 24px 13px 24px;
    }
  `,
  Icon: css`
    display: inline-block;
    vertical-align: middle;
    margin-left: 14px;
    transform: translateY(-2px);
  `,
};

function getThemeStyle(themeName = 'yellow', disabled = false) {
  let theme = ThemeColors[themeName];

  return `
    background-color: ${theme.button};
    border: 2px solid ${theme.button};
    color: ${theme.text};
    cursor: ${disabled ? 'default' : 'pointer'};
    ${MediaQueries.Medium} {
      &:hover {
        background-color: ${theme.buttonHover};
        border: 2px solid ${theme.buttonHover};
        transform: ${disabled ? '' : 'translateY(-1px)'};
        box-shadow: ${theme.buttonHoverShadow} 0 2px 4px;
      }
    }
    &:active {
      transform: ${disabled ? '' : 'translateY(1px)'};
      box-shadow: none;
      background-color: ${theme.buttonActive};
      border: 2px solid ${theme.buttonActive};
    }
  `;
}

function getBorderedThemeStyle(themeName = 'yellow', disabled = false) {
  let theme = ThemeColors[themeName];

  return `
    background-color: transparent;
    border: 2px solid ${theme.button};
    color: ${theme.borderedText};
    cursor: ${disabled ? 'default' : 'pointer'};
    ${MediaQueries.Medium} {
      &:hover {
        border: 2px solid ${theme.buttonHover};
        transform: ${disabled ? '' : 'translateY(-1px)'};
      }
    }
    &:active {
      transform: ${disabled ? '' : 'translateY(1px)'};
    }
  `;
}

const ThemeStyles = {
  regular: {
    enabled: {
      yellow: css`
        ${getThemeStyle('yellow')};
      `,
      indigo: css`
        ${getThemeStyle('indigo')};
      `,
      indigoLight: css`
        ${getThemeStyle('indigoLight')};
      `,
      white: css`
        ${getThemeStyle('white')};
      `,
      violet: css`
        ${getThemeStyle('violet')};
      `,
      gray: css`
        ${getThemeStyle('gray')};
      `,
      red: css`
        ${getThemeStyle('red')};
      `,
      publicStorage: css`
        ${getThemeStyle('publicStorage')};
      `,
      extraSpaceStorage: css`
        ${getThemeStyle('extraSpaceStorage')};
      `,
      a1Storage: css`
        ${getThemeStyle('a1Storage')};
      `,
      shop: css`
        font-size: 26px;
        background: #3a4ba9;
        box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.15), 0 4px 12px 0 rgba(58, 75, 169, 0.25);
        color: ${Colors.White};
        border-radius: 16px;
        padding: 24px 32px;
      `,
    },
    disabled: {
      yellow: css`
        ${getThemeStyle('yellow', true)};
      `,
      indigo: css`
        ${getThemeStyle('indigo', true)};
      `,
      indigoLight: css`
        ${getThemeStyle('indigoLight', true)};
      `,
      white: css`
        ${getThemeStyle('white', true)};
      `,
      violet: css`
        ${getThemeStyle('violet', true)};
      `,
      gray: css`
        ${getThemeStyle('gray', true)};
      `,
      red: css`
        ${getThemeStyle('red', true)};
      `,
      publicStorage: css`
        ${getThemeStyle('publicStorage', true)};
      `,
      extraSpaceStorage: css`
        ${getThemeStyle('extraSpaceStorage', true)};
      `,
      a1Storage: css`
        ${getThemeStyle('a1Storage', true)};
      `,
    },
  },
  bordered: {
    enabled: {
      yellow: css`
        ${getBorderedThemeStyle('yellow')};
      `,
      indigo: css`
        ${getBorderedThemeStyle('indigo')};
      `,
      indigoLight: css`
        ${getBorderedThemeStyle('indigoLight')};
      `,
      white: css`
        ${getBorderedThemeStyle('white')};
      `,
      violet: css`
        ${getBorderedThemeStyle('violet')};
      `,
      gray: css`
        ${getBorderedThemeStyle('gray')};
      `,
      red: css`
        ${getBorderedThemeStyle('red')};
      `,
      publicStorage: css`
        ${getBorderedThemeStyle('publicStorage')};
      `,
      extraSpaceStorage: css`
        ${getBorderedThemeStyle('extraSpaceStorage')};
      `,
      a1Storage: css`
        ${getBorderedThemeStyle('a1Storage')};
      `,
    },
    disabled: {
      yellow: css`
        ${getBorderedThemeStyle('yellow', true)};
      `,
      indigo: css`
        ${getBorderedThemeStyle('indigo', true)};
      `,
      indigoLight: css`
        ${getBorderedThemeStyle('indigoLight', true)};
      `,
      white: css`
        ${getBorderedThemeStyle('white', true)};
      `,
      violet: css`
        ${getBorderedThemeStyle('violet', true)};
      `,
      gray: css`
        ${getBorderedThemeStyle('gray', true)};
      `,
      red: css`
        ${getBorderedThemeStyle('red', true)};
      `,
      publicStorage: css`
        ${getBorderedThemeStyle('publicStorage', true)};
      `,
      extraSpaceStorage: css`
        ${getBorderedThemeStyle('extraSpaceStorage', true)};
      `,
      a1Storage: css`
        ${getBorderedThemeStyle('a1Storage', true)};
      `,
    },
  },
};

export default Button;



// WEBPACK FOOTER //
// ./src/ui/components/core/Button.js